/**
 * Complete state snapshot of a generated CRUD form, published via
 * {@code MvsCrudObjectFormComponent#onFormStateChange}.
 *
 * Exists for the "the caller owns the save" constellation: the form runs with
 * {@code hideDefaultActions}, the embedding component renders its OWN action button and therefore
 * needs the same verdicts the built-in footer buttons evaluate internally — without re-implementing
 * any field check.
 *
 * The two pre-existing outputs stay untouched and are strict subsets of this one:
 * {@code onFormDirty} = {@link #dirty}, {@code onFormSubmitStateChange} = {@link #submittable}.
 * The extra flags exist so a caller can tell the CAUSE apart: a disabled action button because the
 * template is incomplete ({@link #complete} = false) is a different situation from one because a
 * mandatory field is empty ({@link #valid} = false), and a wizard usually wants to say so.
 */
export class MvsFormStateDto {

    /** The form group has unsaved user changes. Reset to false whenever the group is rebuilt. */
    dirty: boolean = false;

    /** Angular validity of the whole form group (all validators from the form metadata). */
    valid: boolean = false;

    /** Server verdict {@code MvsFormDto#formComplete} — the template itself is usable. */
    complete: boolean = false;

    /** A template refresh or a write is currently in flight. */
    busy: boolean = false;

    /** The form was switched off by its caller. */
    disabled: boolean = false;

    /** The form renders read-only. */
    readonly: boolean = false;

    /** No object id yet — a trigger performs a CREATE, otherwise an UPDATE. */
    createMode: boolean = false;

    /**
     * The save/create may be triggered right now: {@code complete && valid && !busy && !disabled}.
     * Identical to the value published on {@code onFormSubmitStateChange} and the exact condition
     * {@code MvsCrudObjectFormComponent#triggerAction} enforces.
     */
    submittable: boolean = false;

    static create(values: Partial<MvsFormStateDto>): MvsFormStateDto {
        const state = new MvsFormStateDto();
        Object.assign(state, values);
        return state;
    }

    /** Value equality — the emitter publishes only on real changes. */
    equals(other: MvsFormStateDto): boolean {

        if (!other) {
            return false;
        }

        return this.dirty === other.dirty
            && this.valid === other.valid
            && this.complete === other.complete
            && this.busy === other.busy
            && this.disabled === other.disabled
            && this.readonly === other.readonly
            && this.createMode === other.createMode
            && this.submittable === other.submittable;
    }
}
